import React from 'react';
import { EventTypes } from 'utils/constants/eventTypes';

export interface IAuthData {
  email: string;
  password: string;
  userType?: string;
  passwordConfirm?: string;
  referralOwnerId?: number;
}
type Error = { message?: string; statusCode?: number };

export type UserResponce = {
  accessToken?: string;
  user?: IUser;
};
export interface IAuthState {
  user?: UserResponce;
  loginError?: Error;
  signUpError?: Error;
  isSignedIn: boolean;
  newUser: boolean;
  rootLink: string;
}
export interface IAnetaPacks {
  anetaPackType: string;
  avatar: string;
  createdById: number;
  grades: string[];
  interests?: string[];
  skills?: string[];
  id: number;
  name: string;
  parentId: number;
  settings: null;
  startTime: string;
  type: string;
  userId: number;
}
export interface ICurrentAnetaPack {
  anetaPack: IAnetaPacks;
  digitalResources: IDResource[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  events: any;
}
export type AnetapackOptions = {
  homeroom: boolean;
  schedule: boolean;
};
export interface IAnetaPackState {
  anetaPacks: IAnetaPacks[];
  currentAnetaPack: ICurrentAnetaPack | undefined;
  anetapackOptions: AnetapackOptions;
  errors: string[];
}
export enum MapTemplate {
  littlePigs = 'littlePigs',
  fantasyLand = 'fantasyLand',
  undersea = 'undersea',
  futureCity = 'futureCity',
  design5 = 'design5',
  design6 = 'design6',
  design7 = 'design7',
  design8 = 'design8',
}
export enum MapType {
  Linear = 'linear',
  Static = 'static',
}

export interface IUnitMapSettings {
  template: MapTemplate;
  type: MapType;
}

export type UnitSettings = {
  permissionToEditResources: boolean;
  permissionToEditTimetable: boolean;
  mapTemplate: MapTemplate;
  mapType: MapType;
};
export interface IScholarsRequestData {
  id?: number;
  name: string;
  grades: string[];
  avatar?: string;
  settings?: UnitSettings;
}

export interface ICreateAnetapack {
  id?: number;
  name: string;
  grades: string[];
  skills: string[];
  interests: string[];
  avatar?: string;
  settings?: UnitSettings;
}

export interface IOverrideData {
  anetaPackId: number;
  startTime: string /* "2021-05-20 00:00" */;
  schedule: boolean;
  homeroom: boolean;
  repeat: number;
}

export enum UserSubscriptionStatus {
  /**
   * Used during the whole period when a user is on trial.
   * Positive status that assumes granting access to the app the user.
   */
  TRIALING = 'trialing',
  /**
   * Used when a user has a regular, paid, valid subscription.
   * Positive status that assumes granting access to the app the user.
   */
  ACTIVE = 'active',
  /**
   * Used when a user has a subscription that has not been paid and therefore invalid.
   * Negative status that assumes suspending user access to the app.
   */
  UNPAID = 'unpaid',
  /**
   * Used when a user has a regular, canceled subscription.
   * Negative status that assumes suspending user access to the app.
   */
  CANCELED = 'canceled',
  /**
   * Used when a user's subscription will be canceled at the end of the paid period.
   * Positive status that assumes granting access to the app the user.
   */
  CANCELING = 'canceling',
  /**
   * Used when a user does not have any regular subscription.
   * Negative status that assumes suspending user access to the app.
   */
  NONE = 'none',
}

export interface IUserSubscriptionData {
  stripeCustomerId: string;
  subscriptionStatus: UserSubscriptionStatus;
  subscriptionPlanId: string;
  subscriptionExpiration: number;
  subscriptionTrialExpiration: number;
}

export interface IUser extends Partial<IUserSubscriptionData> {
  id: string;
  email: string;
  type: string;
  scholars: IScholars[];
  activeScholarId?: number;
}

export type ParentUser = IUser & IUserSubscriptionData;

export interface IScholars {
  id: number;
  name: string;
  grades: string[];
  interests?: string[];
  skills?: string[];
  avatar: string | null;
  permissionToEditTimetable?: boolean;
  permissionToEditResources?: boolean;
  user?: IUser;
  settings?: UnitSettings;
}
export type UnitStats = {
  recipients: number;
  timesApplied: number;
  emails: string[];
};
export interface IScholarsState {
  scholar: IScholars | null;
  units: IScholars[] | null;
  stats: UnitStats | null;
  errors: string;
  anetapackError: string[];
}
export interface IUserState {
  userData?: IUser | ParentUser | null;
  errors: string[];
}
export type EventStats = {
  date: string | undefined;
  duration: number;
  isOnline: boolean;
  id: number | undefined;
  attended: boolean | undefined;
};
export interface IEventsState {
  eventsList: IEvents[];
  mergeEvents: IEvents[];
  errors: [] | string[];
}
export type Time = {
  from?: string;
  to?: string;
};
export interface IEvents {
  id?: number;
  title: string;
  subject: EventTypes;
  date?: string;
  from?: string;
  to?: string;
  startTime: string;
  endTime: string;
  link: string | null;
  repeat: string | null;
  isOnline: boolean;
  endTimeStr?: string;
  startTimeStr?: string;
  // TODO: do we need unit here?
  unit?: IScholars;
  isStarted?: boolean;
}

export interface IDResource {
  id: number;
  link: string;
  type: string | null;
  ownerType?: string;
  unitId?: number;
}
export interface Resource extends IDResource {
  isNewItem?: boolean;
}
export interface IDResourcesState {
  units: {
    [unitId: number]: IDResource[];
  };
}

export interface ISigninResource {
  id: number;
  url: string;
  tested: boolean;
}

export interface ISigninResourcesState {
  units: {
    [unitId: number]: ISigninResource[];
  };
}

export interface IShareAnetaPackData {
  unitId: number;
  startTime: string;
  emails: string[];
}

export type LiteracyModalResult = boolean | null;
// Interface for global UI properties like preloader or modals

export interface IAppStateState {
  literacyModalActive: boolean;
  literacyModalResult: LiteracyModalResult;
  showStatusOfflineSpinner: boolean;
  offlineSpinnerUnitId?: number;
  globalError?: string;
}

export interface IChangeUserEmailChange {
  newEmail: string;
}

export interface IError {
  error: string;
  statusCode: number;
}

export interface CheckmarksProps {
  process: boolean;
  visited: boolean;
  position: {
    x: number;
    y: number;
  };
  gradientIdChunk: string;
}

export type IMapTemplates<T> = {
  [key in MapTemplate]: T;
};

export interface ISubjects {
  art: React.FC<IMapLessonItemStates>;
  cafeteria: React.FC<IMapLessonItemStates>;
  gym: React.FC<IMapLessonItemStates>;
  math: React.FC<IMapLessonItemStates>;
  languageArt: React.FC<IMapLessonItemStates>;
  library: React.FC<IMapLessonItemStates>;
  makersPlace: React.FC<IMapLessonItemStates>;
  multiPurpose: React.FC<IMapLessonItemStates>;
  music: React.FC<IMapLessonItemStates>;
  playGround: React.FC<IMapLessonItemStates>;
  science: React.FC<IMapLessonItemStates>;
  social: React.FC<IMapLessonItemStates>;
}

export type MapGroupProps = {
  resources: IDResource[];
  onHoveredElementChanged: (
    resource: IDResource | undefined,
    element: SVGElement | undefined
  ) => void;
  onClick: () => void;
};

export interface IDRLinks {
  art: React.FC<MapGroupProps>;
  languageArts: React.FC<MapGroupProps>;
  math: React.FC<MapGroupProps>;
  miscellaneous: React.FC<MapGroupProps>;
  science: React.FC<MapGroupProps>;
  socialStudies: React.FC<MapGroupProps>;
  technologySkills: React.FC<MapGroupProps>;
}

export interface IMapLessonItemStates {
  active: boolean;
  process: boolean;
  visited: boolean;
}

export type MapLessonItemProps = Partial<IMapLessonItemStates> & {
  url: string;
  finishTime: number;
  svgElement?: React.ReactNode;
  onClickCb?: () => void;
};

export interface StaticMapProps {
  scholar: IScholars | null;
  staticElements: React.ReactElement;
}

export interface StaticRoomsProps {
  literacyCheck: (url: string) => void;
}

export enum LineChartTypes {
  OnlineOfflineComparison = 'onlineOfflineComparison',
  Attendance = 'attendance',
}

export type LineChartGradientSides = {
  left: string;
  right: string;
};

export type LineChartGradientsType = {
  [key in LineChartTypes]: LineChartGradientSides;
};

export type PopupData = {
  clickedChanged: (element: HTMLElement | undefined) => void;
  popupIsOpened: boolean;
};
